import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, API } from "../lib/api";
import useClickOutside from "../lib/useClickOutside";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PartDialog from "../components/PartDialog";
import StockDialog from "../components/StockDialog";
import CameraScanDialog from "../components/CameraScanDialog";
import InlineCostCell from "../components/InlineCostCell";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Plus,
  PencilSimple,
  Trash,
  ArrowsClockwise,
  Barcode,
  FunnelSimple,
  Warning,
  Package,
  DownloadSimple,
  FilePdf,
  FileCsv,
  Camera,
  Archive,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";

const PART_FALLBACK_IMG =
  "https://images.unsplash.com/photo-1715322554946-1b22a9800aec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHx0cmFpbGVyJTIwbWV0YWwlMjBwYXJ0cyUyMGNvbXBvbmVudHN8ZW58MHx8fHwxNzc3OTMzNzE2fDA&ixlib=rb-4.1.0&q=85";

export default function Parts() {
  const { user, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [parts, setParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [lowOnly, setLowOnly] = useState(searchParams.get("low_stock") === "1");
  const [editPart, setEditPart] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [stockPart, setStockPart] = useState(null);
  const [openScan, setOpenScan] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const exportRef = useRef(null);
  useClickOutside(exportRef, () => setExportOpen(false), exportOpen);

  const canManage = hasRole("admin", "warehouse_staff");
  const canEdit = hasRole("admin", "warehouse_staff", "viewer");
  const canAdd = hasRole("admin", "warehouse_staff", "viewer");
  // Barcode/QR scanning is gated on the org's plan tier — Team and Pro only.
  // Demo orgs (plan.features.barcode_scanning === true by default) also keep it.
  const planHasScanning = user?.plan?.features?.barcode_scanning !== false;
  const canScan = hasRole("admin", "warehouse_staff") && planHasScanning;
  const canDelete = hasRole("admin");
  const isAdmin = hasRole("admin");
  const showCost = !hasRole("viewer");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (categoryId) params.category_id = categoryId;
    if (lowOnly) params.low_stock = true;
    if (showDeleted) params.deleted = true;
    try {
      const [{ data: ps }, { data: cs }] = await Promise.all([
        api.get("/parts", { params }),
        categories.length ? Promise.resolve({ data: categories }) : api.get("/categories"),
      ]);
      setParts(ps);
      if (!categories.length) setCategories(cs);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, categoryId, lowOnly, showDeleted]);

  useEffect(() => {
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      if (lowOnly) next.set("low_stock", "1");
      else next.delete("low_stock");
      return next;
    });
    // eslint-disable-next-line
  }, [lowOnly]);

  const onDelete = async (p) => {
    if (!window.confirm(`Archive "${p.name}" (${p.sku})? Transaction history is preserved and the part can be restored later.`)) return;
    try {
      await api.delete(`/parts/${p.id}`);
      toast.success("Part archived");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const onRestore = async (p) => {
    try {
      await api.post(`/parts/${p.id}/restore`);
      toast.success("Part restored");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const focusId = searchParams.get("focus");
  const focusPart = useMemo(() => parts.find((p) => p.id === focusId), [focusId, parts]);

  const downloadExport = async (format) => {
    setExportOpen(false);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/exports/parts?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `parts.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF9800]">Inventory · Catalog</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 mt-1">
            Parts
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{parts.length} records</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setShowDeleted((v) => !v)}
              data-testid="toggle-deleted"
              className={`inline-flex items-center gap-2 border px-4 py-2.5 text-sm font-semibold ${
                showDeleted
                  ? "bg-[#1A2332] text-white border-zinc-950"
                  : "bg-white border-zinc-300 hover:bg-zinc-100"
              }`}
            >
              <Archive size={16} weight="bold" />
              {showDeleted ? "Show active" : "Archived"}
            </button>
          )}
          <div className="relative" ref={exportRef}>
            <button
              onClick={() => setExportOpen((v) => !v)}
              data-testid="export-button"
              className="inline-flex items-center gap-2 border border-zinc-300 bg-white hover:bg-zinc-100 px-4 py-2.5 text-sm font-semibold"
            >
              <DownloadSimple size={16} weight="bold" />
              Export
            </button>
            {exportOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-zinc-300 shadow-lg z-30 w-44">
                <button
                  onClick={() => downloadExport("csv")}
                  data-testid="export-parts-csv"
                  className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-zinc-100 text-sm"
                >
                  <FileCsv size={16} weight="duotone" />
                  CSV spreadsheet
                </button>
                <button
                  onClick={() => downloadExport("pdf")}
                  data-testid="export-parts-pdf"
                  className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-zinc-100 text-sm border-t border-zinc-200"
                >
                  <FilePdf size={16} weight="duotone" />
                  PDF report
                </button>
              </div>
            )}
          </div>
          {canAdd && !showDeleted && (
            <button
              onClick={() => setOpenCreate(true)}
              data-testid="add-part-button"
              className="inline-flex items-center gap-2 bg-[#FF9800] hover:bg-[#E68900] text-white px-5 py-2.5 text-sm font-semibold transition-colors"
            >
              <Plus size={16} weight="bold" />
              Add part
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-zinc-300 p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className={`${canScan ? "md:col-span-5" : "md:col-span-8"} relative`}>
          <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search SKU, part number, name, location, supplier…"
            data-testid="parts-search-input"
            className="w-full pl-10 pr-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] text-sm"
          />
        </div>
        {canScan && (
          <div className="md:col-span-3 relative">
            <Barcode size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#FF9800]" />
            <input
              placeholder="Scan or type SKU…"
              data-testid="parts-scan-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setQ(e.currentTarget.value);
                }
              }}
              className="w-full pl-10 pr-12 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setOpenScan(true)}
              data-testid="open-camera-scan"
              title="Scan with camera"
              className="absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1.5 bg-[#1A2332] hover:bg-[#FF9800] text-white"
            >
              <Camera size={14} weight="bold" />
            </button>
          </div>
        )}
        <div className="md:col-span-3 relative">
          <FunnelSimple size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            data-testid="parts-category-filter"
            className="w-full pl-10 pr-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] text-sm appearance-none bg-white"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <label
          className={`md:col-span-1 flex items-center justify-center gap-2 px-3 py-2.5 cursor-pointer text-xs font-bold uppercase tracking-widest border ${
            lowOnly ? "bg-amber-400 text-amber-950 border-amber-500" : "border-zinc-300 hover:bg-zinc-100"
          }`}
          data-testid="parts-low-stock-toggle"
        >
          <input
            type="checkbox"
            checked={lowOnly}
            onChange={(e) => setLowOnly(e.target.checked)}
            className="hidden"
          />
          <Warning size={14} weight="bold" />
          Low
        </label>
      </div>

      {/* Mobile card view */}
      <div className="md:hidden space-y-3">
        {loading && (
          <div className="bg-white border border-zinc-300 p-8 text-center text-xs uppercase tracking-widest text-zinc-500 font-mono">
            Loading…
          </div>
        )}
        {!loading && parts.length === 0 && (
          <div className="bg-white border border-zinc-300 p-8 text-center">
            <Package size={28} weight="duotone" className="mx-auto text-zinc-300" />
            <div className="text-sm text-zinc-500 mt-2">No parts found.</div>
            {canManage && !showDeleted && (
              <button
                onClick={() => setOpenCreate(true)}
                className="mt-4 inline-flex items-center gap-1 text-[#FF9800] text-sm font-semibold hover:underline"
              >
                <Plus size={14} /> Add your first part
              </button>
            )}
          </div>
        )}
        {parts.map((p) => {
          const isLow = p.stock_qty <= p.low_stock_threshold;
          const isOut = p.stock_qty === 0;
          return (
            <div
              key={p.id}
              data-testid={`part-card-${p.sku}`}
              className="bg-white border border-zinc-300 p-3 flex gap-3"
            >
              <div className="h-14 w-14 bg-zinc-100 border border-zinc-200 overflow-hidden flex-shrink-0">
                <img
                  src={p.image_url || PART_FALLBACK_IMG}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => (e.currentTarget.src = PART_FALLBACK_IMG)}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] font-bold text-zinc-900">{p.sku}</div>
                    <div className="font-medium text-sm text-zinc-900 truncate">{p.name}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center justify-end gap-1">
                      {isOut && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-red-600 text-white">OUT</span>}
                      {!isOut && isLow && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-400 text-amber-950">LOW</span>}
                      <span className={`font-mono font-bold text-base ${isOut ? "text-red-600" : isLow ? "text-amber-600" : "text-zinc-900"}`}>
                        {p.stock_qty}
                      </span>
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono">in stock</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[10px]">
                  <span className="px-1.5 py-0.5 font-bold uppercase tracking-wider bg-zinc-100 text-zinc-700">
                    {p.category_name || "—"}
                  </span>
                  {p.location && (
                    <span className="font-mono text-zinc-500">{p.location}</span>
                  )}
                </div>
                <div className="flex items-end justify-between mt-2 pt-2 border-t border-zinc-100">
                  <div className="text-[10px]">
                    {showCost ? (
                      <>
                        <span className="text-zinc-500 uppercase tracking-widest mr-1">Cost</span>
                        <InlineCostCell part={p} editable={canManage && !showDeleted} onSaved={load} />
                        <span className="text-zinc-500 ml-2">·</span>
                        <span className="font-mono text-xs font-bold text-zinc-700 ml-2">
                          ${(p.stock_qty * p.unit_cost).toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-zinc-500 uppercase tracking-widest mr-1">Threshold</span>
                        <span className="font-mono text-xs text-zinc-700">{p.low_stock_threshold}</span>
                      </>
                    )}
                  </div>
                  <div className="inline-flex items-center divide-x divide-zinc-300 border border-zinc-300">
                    {showDeleted && isAdmin && (
                      <button
                        onClick={() => onRestore(p)}
                        data-testid={`restore-action-${p.sku}`}
                        className="px-2 py-1.5 hover:bg-emerald-600 hover:text-white"
                      >
                        <ArrowCounterClockwise size={14} weight="bold" />
                      </button>
                    )}
                    {!showDeleted && canManage && (
                      <button
                        onClick={() => setStockPart(p)}
                        data-testid={`stock-action-mobile-${p.sku}`}
                        className="px-2 py-1.5 hover:bg-[#FF9800] hover:text-white"
                      >
                        <ArrowsClockwise size={14} weight="bold" />
                      </button>
                    )}
                    {!showDeleted && canEdit && (
                      <button
                        onClick={() => setEditPart(p)}
                        data-testid={`edit-action-mobile-${p.sku}`}
                        className="px-2 py-1.5 hover:bg-[#1A2332] hover:text-white"
                      >
                        <PencilSimple size={14} weight="bold" />
                      </button>
                    )}
                    {!showDeleted && canDelete && (
                      <button
                        onClick={() => onDelete(p)}
                        data-testid={`delete-action-mobile-${p.sku}`}
                        className="px-2 py-1.5 hover:bg-red-600 hover:text-white"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-zinc-300 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900 w-12"></th>
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900">SKU / Part #</th>
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900">Name</th>
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900">Category</th>
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900">Location</th>
              <th className="text-right px-4 py-3 border-b-2 border-zinc-900">Stock</th>
              {showCost && <th className="text-right px-4 py-3 border-b-2 border-zinc-900">Unit cost</th>}
              {showCost && <th className="text-right px-4 py-3 border-b-2 border-zinc-900">Value</th>}
              <th className="text-right px-4 py-3 border-b-2 border-zinc-900 w-40"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={showCost ? 9 : 7} className="px-6 py-12 text-center text-xs uppercase tracking-widest text-zinc-500 font-mono">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && parts.length === 0 && (
              <tr>
                <td colSpan={showCost ? 9 : 7} className="px-6 py-16 text-center">
                  <Package size={32} weight="duotone" className="mx-auto text-zinc-300" />
                  <div className="text-sm text-zinc-500 mt-2">No parts found.</div>
                  {canManage && (
                    <button
                      onClick={() => setOpenCreate(true)}
                      className="mt-4 inline-flex items-center gap-1 text-[#FF9800] text-sm font-semibold hover:underline"
                    >
                      <Plus size={14} /> Add your first part
                    </button>
                  )}
                </td>
              </tr>
            )}
            {parts.map((p) => {
              const isLow = p.stock_qty <= p.low_stock_threshold;
              const isOut = p.stock_qty === 0;
              const focused = focusId === p.id;
              return (
                <tr
                  key={p.id}
                  className={`row-hover ${focused ? "bg-[#FFF3E0]" : ""}`}
                  data-testid={`part-row-${p.sku}`}
                >
                  <td className="px-4 py-3 border-b border-zinc-200">
                    <div className="h-9 w-9 bg-zinc-100 border border-zinc-200 overflow-hidden">
                      <img
                        src={p.image_url || PART_FALLBACK_IMG}
                        alt=""
                        className="h-full w-full object-cover"
                        onError={(e) => (e.currentTarget.src = PART_FALLBACK_IMG)}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-200">
                    <div className="font-mono text-xs font-bold text-zinc-900">{p.sku}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{p.part_number}</div>
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-200">
                    <div className="font-medium text-zinc-900">{p.name}</div>
                    {p.supplier && <div className="text-[11px] text-zinc-500">{p.supplier}</div>}
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-200">
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-700">
                      {p.category_name || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-200 font-mono text-xs text-zinc-600">
                    {p.location || "—"}
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-200 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {isOut ? (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-red-600 text-white">OUT</span>
                      ) : isLow ? (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-400 text-amber-950">LOW</span>
                      ) : null}
                      <span className={`font-mono font-bold ${isOut ? "text-red-600" : isLow ? "text-amber-600" : "text-zinc-900"}`}>
                        {p.stock_qty}
                      </span>
                    </div>
                  </td>
                  {showCost && (
                    <td className="px-4 py-3 border-b border-zinc-200 text-right">
                      <InlineCostCell
                        part={p}
                        editable={canManage && !showDeleted}
                        onSaved={load}
                      />
                    </td>
                  )}
                  {showCost && (
                    <td className="px-4 py-3 border-b border-zinc-200 text-right font-mono text-xs font-bold">
                      ${(p.stock_qty * p.unit_cost).toFixed(2)}
                    </td>
                  )}
                  <td className="px-4 py-3 border-b border-zinc-200 text-right">
                    <div className="inline-flex items-center divide-x divide-zinc-300 border border-zinc-300">
                      {showDeleted && isAdmin && (
                        <button
                          onClick={() => onRestore(p)}
                          title="Restore"
                          data-testid={`restore-action-${p.sku}`}
                          className="px-2.5 py-1.5 hover:bg-emerald-600 hover:text-white"
                        >
                          <ArrowCounterClockwise size={14} weight="bold" />
                        </button>
                      )}
                      {!showDeleted && canManage && (
                        <button
                          onClick={() => setStockPart(p)}
                          title="Adjust stock"
                          data-testid={`stock-action-${p.sku}`}
                          className="px-2.5 py-1.5 hover:bg-[#FF9800] hover:text-white"
                        >
                          <ArrowsClockwise size={14} weight="bold" />
                        </button>
                      )}
                      {!showDeleted && canEdit && (
                        <button
                          onClick={() => setEditPart(p)}
                          title="Edit"
                          data-testid={`edit-action-${p.sku}`}
                          className="px-2.5 py-1.5 hover:bg-[#1A2332] hover:text-white"
                        >
                          <PencilSimple size={14} weight="bold" />
                        </button>
                      )}
                      {!showDeleted && canDelete && (
                        <button
                          onClick={() => onDelete(p)}
                          title="Archive"
                          data-testid={`delete-action-${p.sku}`}
                          className="px-2.5 py-1.5 hover:bg-red-600 hover:text-white"
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <PartDialog
        open={openCreate || !!editPart}
        onClose={() => {
          setOpenCreate(false);
          setEditPart(null);
        }}
        part={editPart}
        categories={categories}
        onSaved={() => {
          setOpenCreate(false);
          setEditPart(null);
          load();
        }}
      />

      <StockDialog
        open={!!stockPart || !!focusPart}
        onClose={() => {
          setStockPart(null);
          if (focusId) {
            const next = new URLSearchParams(searchParams);
            next.delete("focus");
            setSearchParams(next);
          }
        }}
        part={stockPart || focusPart}
        onSaved={() => {
          setStockPart(null);
          if (focusId) {
            const next = new URLSearchParams(searchParams);
            next.delete("focus");
            setSearchParams(next);
          }
          load();
        }}
      />

      {canScan && (
        <CameraScanDialog
          open={openScan}
          onClose={() => setOpenScan(false)}
          onResult={(code) => {
            setOpenScan(false);
            setQ(code);
            toast.success(`Scanned: ${code}`);
          }}
        />
      )}
    </div>
  );
}
